/**
 * DragGhost.tsx
 * Visual overlays for an in-progress booking drag (Machine Schedule UX — M2).
 *
 * - <DragGhost>: a dashed preview bar rendered inside the *target* machine row
 *   at the proposed position. Green-ish when the drop is valid, red when the
 *   target machine can't run the booking's task type.
 * - <DragDateTooltip>: a small label that follows the pointer showing the
 *   proposed start → end range, duration in days, and (when moving) the target
 *   machine code.
 *
 * Pure presentational; all colors come from theme.schedule.* (no hex literals).
 */

import React from 'react';
import { Box } from '@mui/material';
import { useTheme, alpha, type Theme } from '@mui/material/styles';

// ── DragGhost ────────────────────────────────────────────────────────────────

interface DragGhostProps {
  /** Left offset as a fraction (0–1) of the visible day window. */
  leftFraction: number;
  /** Width as a fraction (0–1) of the visible day window. */
  widthFraction: number;
  /** False when the hovered machine can't run the booking's task type. */
  valid: boolean;
}

export const DragGhost: React.FC<DragGhostProps> = ({ leftFraction, widthFraction, valid }) => {
  const theme = useTheme();
  const color = valid ? theme.schedule.committed : theme.schedule.conflictHard;
  return (
    <Box
      data-testid="drag-ghost"
      sx={{
        position: 'absolute',
        top: '4px',
        bottom: '4px',
        left: `${leftFraction * 100}%`,
        width: `${widthFraction * 100}%`,
        minWidth: 4,
        borderRadius: '3px',
        border: `2px dashed ${color}`,
        backgroundColor: alpha(color, 0.18),
        boxShadow: `0 0 0 1px ${alpha(color, 0.25)}`,
        pointerEvents: 'none',
        zIndex: 5,
        boxSizing: 'border-box',
      }}
    />
  );
};

// ── DragDateTooltip ────────────────────────────────────────────────────────────

interface DragDateTooltipProps {
  x: number;
  y: number;
  start: string;
  end: string;
  /** Target machine code, shown only when a move changes machine. */
  machineCode?: string | null;
  valid: boolean;
}

function fmt(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
  });
}

function inclusiveDays(start: string, end: string): number {
  const a = new Date(`${start}T00:00:00`).getTime();
  const b = new Date(`${end}T00:00:00`).getTime();
  return Math.round((b - a) / 86_400_000) + 1;
}

export const DragDateTooltip: React.FC<DragDateTooltipProps> = ({
  x,
  y,
  start,
  end,
  machineCode,
  valid,
}) => {
  const theme: Theme = useTheme();
  const bg = valid ? theme.schedule.committed : theme.schedule.conflictHard;
  const days = inclusiveDays(start, end);
  return (
    <Box
      data-testid="drag-date-tooltip"
      sx={{
        position: 'fixed',
        left: x + 14,
        top: y + 14,
        zIndex: theme.zIndex.tooltip,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        backgroundColor: bg,
        color: theme.palette.getContrastText(bg),
        fontSize: '0.7rem',
        fontWeight: 600,
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        boxShadow: theme.shadows[3],
      }}
    >
      {fmt(start)} → {fmt(end)} · {days}d
      {machineCode ? ` · ${machineCode}` : ''}
    </Box>
  );
};
